import React, { useEffect, useRef } from "react";
import useMobileMode from "@/hooks/useMobileMode";
import MobileShell from "@/components/mobile/MobileShell";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFocusFromUrl } from "@/lib/deeplink";
import { canSee, ROLE_LABELS, effectiveRoles, moduleEnabledForNav } from "@/lib/perms";
import CompanySwitcher from "./CompanySwitcher";
import { NAV } from "./navConfig";
import { LogOut, ChevronRight } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import InstallPWA from "./InstallPWA";
import GlobalSearch from "./GlobalSearch";
import NotificationsBell from "./NotificationsBell";

// "My Workspace" items act on the signed-in user's own EMPLOYEE record. An
// account with no employee linked (a pure super_admin, which is what a fresh
// deployment ships with) has nothing for them to act on: /me/leaves,
// /me/overtime and /me/payslips all 404 and clock-status 422s. Hide them
// rather than showing a section that cannot work.
const SELF_SERVICE_KEYS = new Set([
    "my_dashboard", "my_profile", "clock_in", "my_time",
    "my_payslips", "my_learning", "my_requests", "my_tickets",
]);
const hasEmployeeProfile = (u) => Boolean(u && (u.employee_id || u.employeeId));
// Some entries share a permission key with a sibling that does NOT need an
// employee record (/rewards works for an admin, /rewards/exchange does not and
// errors with "Your account has no employee record."), so those are matched by
// path rather than key.
const SELF_SERVICE_PATHS = new Set(["/rewards/exchange"]);

export default function AppShell() {
    useFocusFromUrl();
    const isMobile = useMobileMode();
    const { user, tenant, activeCompanyId, logout, companies } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const contentRef = useRef(null);
    // .app-content is the scroller and survives route changes, so a new module
    // would otherwise open at the previous module's scroll offset.
    useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [pathname]);

    if (isMobile) return <MobileShell />;

    const activeCompany = (companies || []).find((c) => c.id === activeCompanyId) || null;
    const roles = Array.from(effectiveRoles(user, activeCompanyId));
    const visibleSections = NAV.map((sec) => ({
        ...sec,
        items: sec.items.filter((it) => canSee(user, activeCompanyId, it.key) && moduleEnabledForNav(it.key, activeCompany) && ((!SELF_SERVICE_KEYS.has(it.key) && !SELF_SERVICE_PATHS.has(it.to)) || hasEmployeeProfile(user))),
    })).filter((sec) => sec.items.length > 0);

    return (
        <div className="app-shell">
            <aside className="app-sidebar flex flex-col" data-testid="app-sidebar">
                <div className="px-4 h-14 flex items-center gap-2 border-b" style={{ borderColor: "hsl(var(--sidebar-border))" }}>
                    <img src="/sahod-mark.png" alt="SAHOD" className="size-8" />
                    <div>
                        <div className="font-display font-bold text-sm leading-none" style={{ color: "hsl(var(--primary))" }}>SAHOD</div>
                        <div className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(var(--sidebar-muted))" }}>{tenant?.name || "Salary & HR Ops"}</div>
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto py-3 px-2 text-sm">
                    {visibleSections.map((sec) => (
                        <div key={sec.section} className="mb-3">
                            <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "hsl(var(--sidebar-muted))" }}>{sec.section}</div>
                            {sec.items.map((it) => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    end={it.to === "/me"}
                                    className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] mt-0.5 ${isActive ? "active" : ""}`}
                                    data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
                                >
                                    <it.icon className="size-4" />
                                    <span>{it.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>
                <div className="px-3 py-2 text-[10px]" style={{ color: "hsl(var(--sidebar-muted))" }}>
                    <div>{roles.map((r) => ROLE_LABELS[r] || r).join(" · ")}</div>
                    <div className="mt-0.5">v1.0.0 · Philippine HRIS & Payroll</div>
                </div>
            </aside>

            <div className="app-shell-main">
                <div className="app-topbar">
                    <CompanySwitcher />
                    <GlobalSearch />
                    <NotificationsBell />
                    <InstallPWA />
                    <DropdownMenu>
                        <DropdownMenuTrigger className="h-9 px-2 flex items-center gap-2 rounded-md border border-border bg-background hover:bg-secondary" data-testid="user-menu">
                            <div className="size-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">{user?.name?.[0] || "?"}</div>
                            <div className="text-left hidden md:block">
                                <div className="text-[12px] font-medium leading-none">{user?.name}</div>
                                <div className="text-[10px] text-muted-foreground">{roles.map((r) => ROLE_LABELS[r] || r).join(", ")}</div>
                            </div>
                            <ChevronRight className="size-4 text-muted-foreground rotate-90" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate("/me/profile")}>My Profile</DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"><LogOut className="size-4 mr-2" /> Logout</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="app-content fade-in" ref={contentRef}><Outlet /></div>
            </div>
        </div>
    );
}
